import { useMemo, useState } from 'react';
import { CURRENT_USER_ID, TODAY, categories, conditions as initialConditions } from '../data/conditions';
import { daysPastEta, getEscalation, isResolved } from '../utils/conditions';
import type { AttentionFilter, Condition, ConditionStatus } from '../types/conditions';

export type ConditionPatch = Partial<Omit<Condition, 'id' | 'comments'>>;

export function useConditions() {
  const [conditions, setConditions] = useState<Condition[]>(initialConditions);
  const [assigneeFilter, setAssigneeFilter] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [attentionFilter, setAttentionFilter] = useState<AttentionFilter>('all');
  const [statusFilter, setStatusFilter] = useState<ConditionStatus[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [commentConditionId, setCommentConditionId] = useState<string | null>(null);
  const [titleNotesAdded, setTitleNotesAdded] = useState(false);
  const [linkConditionId, setLinkConditionId] = useState<string | null>(null);

  /** Conditions matching search + assignee — drives the summary counts. */
  const filteredConditions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return conditions.filter(
      (c) =>
        (assigneeFilter.length === 0 || assigneeFilter.includes(c.assigneeId)) &&
        (!q || c.name.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)),
    );
  }, [conditions, assigneeFilter, searchQuery]);

  const visibleConditions = filteredConditions.filter((c) => {
    if (statusFilter.length > 0 && !statusFilter.includes(c.status)) return false;
    if (attentionFilter === 'overdue') return daysPastEta(c) > 0;
    if (attentionFilter === 'escalated') return getEscalation(c) !== 'none';
    return true;
  });

  const outstandingGroups = categories.map((cat) => ({
    ...cat,
    conditions: visibleConditions.filter((c) => c.category === cat.id && !isResolved(c.status)),
  }));
  const cleared = visibleConditions.filter((c) => isResolved(c.status));

  const commentCondition = conditions.find((c) => c.id === commentConditionId) ?? null;

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll(ids: string[]) {
    setSelectedIds((prev) => {
      const allSelected = ids.every((id) => prev.has(id));
      const next = new Set(prev);
      ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  }

  function updateCondition(id: string, patch: ConditionPatch) {
    setConditions((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        const next = { ...c, ...patch };
        if (patch.status && patch.status !== c.status) {
          next.clearedAt = isResolved(patch.status) ? TODAY : undefined;
        }
        return next;
      }),
    );
  }

  function uploadDocument(id: string) {
    setConditions((prev) => prev.map((c) => (c.id === id ? { ...c, documents: c.documents + 1 } : c)));
  }

  function linkDocuments(id: string, documentIds: string[]) {
    setConditions((prev) =>
      prev.map((c) =>
        c.id === id
          ? { ...c, linkedDocumentIds: Array.from(new Set([...(c.linkedDocumentIds ?? []), ...documentIds])) }
          : c,
      ),
    );
  }

  function addComment(id: string, body: string, mentions: string[], sentToBorrower: boolean) {
    setConditions((prev) =>
      prev.map((c) =>
        c.id === id
          ? {
              ...c,
              comments: [
                ...c.comments,
                {
                  id: `cm-${Date.now()}`,
                  authorId: CURRENT_USER_ID,
                  body,
                  createdAt: new Date().toISOString().slice(0, 19),
                  mentions,
                  sentToBorrower,
                },
              ],
            }
          : c,
      ),
    );
  }

  return {
    assigneeFilter,
    setAssigneeFilter,
    searchQuery,
    setSearchQuery,
    attentionFilter,
    setAttentionFilter,
    statusFilter,
    setStatusFilter,
    titleNotesAdded,
    setTitleNotesAdded,
    filteredConditions,
    outstandingGroups,
    cleared,
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    updateCondition,
    addComment,
    uploadDocument,
    linkDocuments,
    linkCondition: conditions.find((c) => c.id === linkConditionId) ?? null,
    openLinkDocuments: setLinkConditionId,
    closeLinkDocuments: () => setLinkConditionId(null),
    commentCondition,
    openComments: setCommentConditionId,
    closeComments: () => setCommentConditionId(null),
  };
}
